import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { DataTable, type ColumnConfig } from '@/src/components/manage/tables/DataTable'
import { listWorkflows, rollWorkflowShareId, upsertWorkflow } from '@/src/api/workflow'
import { ShareLinkCell } from '@/src/components/manage/tables/CopyableCell'
import { Badge } from '@/src/components/ui/badge'
import { EditAction } from '@/src/components/manage/actions/EditAction'
import { EnabledAction } from '@/src/components/manage/actions/EnabledAction'
import { useTranslation } from '@/src/i18n/client'
import { useAuth } from '@/src/components/AuthContextProvider'
import type { Workflow } from '@/src/types/workflow.zod'
import { PERMISSIONS } from '@/permission.config'
import { CreateAction } from '@/src/components/manage/actions'
import { GenericAction } from '@/src/components/manage/actions/GenericAction'
import { UploadIcon } from '@phosphor-icons/react'

export const Route = createFileRoute('/_manage/manage/workflows/')({
  component: UserWorkflowsPage,
})

function UserWorkflowsPage() {
  const { t } = useTranslation('manage');
  const queryClient = useQueryClient()
  const { user, isAdmin, permissions } = useAuth()
  const navigate = useNavigate()

  // Check permission client-side
  useEffect(() => {
    const hasAccess = permissions.includes(PERMISSIONS.WORKFLOWS_MANAGE) || permissions.includes('*') || isAdmin
    if (!hasAccess) {
      navigate({ to: '/not-permitted' })
    }
  }, [permissions, isAdmin, navigate])
  const { data: allWorkflows, isLoading, error } = useQuery({
    queryKey: ['workflow', 'list'],
    queryFn: () => listWorkflows({ data: {} }),
  })

  const workflows = allWorkflows || []

  const rollShareIdMutation = useMutation({
    mutationFn: (workflowId: string) => rollWorkflowShareId({ data: { workflowId } }),
  })

  const toggleEnabledMutation = useMutation({
    mutationFn: ({ workflow, enabled }: { workflow: Workflow; enabled: boolean }) =>
      upsertWorkflow({
        data: {
          id: workflow.id,
          name: workflow.name,
          description: workflow.description ?? null,
          data: workflow.data,
          visibility: workflow.visibility,
          enabled,
          order: workflow.order,
          isSystem: workflow.isSystem,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow', 'list'] })
    },
  })

  const columns: ColumnConfig<Workflow>[] = [
    {
      key: 'shareId',
      label: t('table.shareId'),
      width: 'w-24',
      optional: true,
      render: (workflow) => {
        const ownerId = workflow.createdById
        const canRotate = isAdmin || (!!ownerId && ownerId === user?.id)
        return (
          <ShareLinkCell
            shareId={workflow.shareId}
            reserveActionSpace={true}
            canRotate={canRotate}
            onRotate={
              canRotate
                ? async () => {
                  await rollShareIdMutation.mutateAsync(workflow.id)
                  await queryClient.invalidateQueries({ queryKey: ['workflow', 'list'] })
                }
                : undefined
            }
          />
        )
      },
    },
    {
      key: 'name',
      label: t('table.name'),
      primary: true,
      sortable: true,
      width: 'w-40',
    },
    {
      key: 'description',
      label: t('table.description'),
      truncate: true,
      optional: true,
      render: (workflow) => workflow.description || '-',
      width: '*',
    },
    {
      key: 'visibility',
      label: t('table.visibility'),
      optional: true,
      render: (workflow) => (
        <Badge
          variant={
            workflow.visibility === "PUBLIC"
              ? "default"
              : workflow.visibility === "ORGANIZATION"
                ? "secondary"
                : "outline"
          }
          className={
            workflow.visibility === "PUBLIC"
              ? "mr-2 px-2 py-0.5 rounded-full text-xs font-semibold align-middle bg-green-100 text-green-800 border-green-200"
              : workflow.visibility === "ORGANIZATION"
                ? "mr-2 px-2 py-0.5 rounded-full text-xs font-semibold align-middle bg-blue-100 text-blue-800 border-blue-200"
                : "mr-2 px-2 py-0.5 rounded-full text-xs font-semibold align-middle bg-gray-100 text-gray-800 border-gray-200"
          }
        >
          {workflow.visibility}
        </Badge>
      ),
    },
    {
      key: 'enabled',
      label: t('table.status'),
      align: 'right',
      render: (workflow) => (
        <EnabledAction
          enabled={!!workflow.enabled}
          onToggle={async (enabled) => {
            await toggleEnabledMutation.mutateAsync({ workflow, enabled })
          }}
        />
      ),
    },
  ];

  return (
    <div>
      <AdminToolbar>
        <AdminToolbar.Left>
          <CreateAction to="/manage/workflows/$id" params={{ id: 'new' }} label={t('workflows.createWorkflow')} />
        </AdminToolbar.Left>
        <AdminToolbar.Right>
          <GenericAction
            to="/manage/workflows/$id"
            params={{ id: 'new' }}
            label={t('workflows.importButton')}
            icon={UploadIcon}
            variant="secondary"
            size="lg"
          />
        </AdminToolbar.Right>
      </AdminToolbar>
      <div className="bg-background text-foreground">
        {error && (
          <p className="text-destructive">
            {error instanceof Error ? error.message : t('workflows.failedToLoadWorkflows')}
          </p>
        )}
        <DataTable<Workflow>
          data={(workflows as any)}
          columns={columns}
          isLoading={isLoading}
          error={error instanceof Error ? error.message : undefined}
          emptyMessage={t('workflows.emptyMessage')}
          actions={(workflow) => (
            <EditAction
              to="/manage/workflows/$id"
              params={{ id: workflow.id }}
            />
          )}
        />
      </div>
    </div>
  )
}
